import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    DESKTOP_MENU_PANEL_ID,
    FOCUSABLE_MENU_ITEM_SELECTOR,
} from "web/react/components/navigation/navigation.constants";
import { useNavigationContext } from "web/react/components/navigation/navigation.context";
import { MenuSerializer, SubMenuSerializer } from "web/types/serializers";
import { CategoryMenu } from "./category-menu";
import { StickyCategories } from "./sticky-categories";

// categories past the display limit stay in the DOM but hidden, so they are not
// tab stops and must not count as the last item in the panel
function getLastPanelItem(panel: HTMLElement): HTMLElement | undefined {
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_MENU_ITEM_SELECTOR))
        .filter((item) => !item.closest('[style*="display: none"]'))
        .pop();
}

export interface DesktopMenuProps {
    menu: MenuSerializer;
    featuredDesignersUrl: string;
}

export function DesktopMenu({ menu, featuredDesignersUrl }: DesktopMenuProps): React.ReactElement {
    const [selectedCategory, setSelectedCategory] = useState<SubMenuSerializer | null>(null);

    const { showMenu, setShowMenu } = useNavigationContext();

    const menuRef = useRef<HTMLDivElement>(null);

    const closeMenu = useCallback(
        function (): void {
            setSelectedCategory(null);
            setShowMenu(null);
        },
        [setShowMenu]
    );

    useEffect(() => {
        if (showMenu !== "desktop") {
            return;
        }

        function onKeyDown(event: KeyboardEvent): void {
            if (event.key === "Tab") {
                const panel = document.getElementById(DESKTOP_MENU_PANEL_ID);

                // do nothing for any element that is not the last menu item
                //
                // also watch for shift key so we don't incorrectly target users going back in menu
                // (shift + tab)
                if (event.shiftKey || !panel || event.target !== getLastPanelItem(panel)) {
                    return;
                }

                const expandedElement =
                    menuRef.current?.querySelector<HTMLElement>('[aria-expanded="true"]');

                if (expandedElement) {
                    expandedElement.focus();
                    // prevent tab event from leaving the nav when we want to return focus
                    // to top level menu
                    event.preventDefault();
                }
                return;
            }

            if (event.key !== "Escape") {
                return;
            }

            const focusWasInsideMenu = menuRef.current?.contains(document.activeElement);
            const expandedElement =
                menuRef.current?.querySelector<HTMLElement>('[aria-expanded="true"]');

            closeMenu();

            // return focus to currently expanded item
            if (focusWasInsideMenu) {
                expandedElement?.focus();
            }
        }

        window.addEventListener("keydown", onKeyDown);

        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showMenu, closeMenu]);

    const onFocusLeave = useCallback(
        function (event: React.FocusEvent<HTMLDivElement>): void {
            const focusMovedWithinMenu = event.currentTarget.contains(event.relatedTarget);

            if (focusMovedWithinMenu) {
                return;
            }

            closeMenu();
        },
        [closeMenu]
    );

    return (
        <>
            <div
                ref={menuRef}
                data-testid="desktop-menu"
                onMouseLeave={closeMenu}
                onBlur={onFocusLeave}
            >
                <StickyCategories
                    menus={menu.menus}
                    links={menu.links}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                />
                <CategoryMenu
                    menuData={menu}
                    featuredDesignersUrl={featuredDesignersUrl}
                    selectedCategory={selectedCategory}
                />
            </div>
        </>
    );
}
